import { format, parseISO } from "date-fns";
import { renderCurrencyWithoutDecimals } from "misc/renderHelpers";
import { ReactElement } from "react";
import { TranslateFunction } from "react-admin";
import { Area, Bar, Cell, Line, ReferenceLine } from "recharts";
import { Payload } from "recharts/types/component/DefaultTooltipContent";

import { InputChoiceType } from "../../App.types";
import {
  dateFromDatetime,
  getEventStatusColor,
  getWeekNumber,
  nonBreakingSpace,
  shortDateFromDatetime,
  shortenAmount,
} from "../../misc/formatHelpers";
import { BookingsOverviewRecordType } from "../../molecules/Charts/BookingsOverviewChart/BookingsOverviewChart.types";
import { GranularityFilterEnum, TimeRangeFilterEnum } from "../../organisms/EventCardList/EventCardList.types";
import { EventStatusEnum } from "../../pages/Event/Event.types";
import { ReportByMainDataApiDataType } from "../../pages/Reporting/Reporting.types";
import {
  ChartAreaType,
  ChartBarType,
  ChartDefsType,
  ChartGradientType,
  ChartLineType,
  ChartReferenceLineType,
  CommonChartDataType,
  ExtendsNumberConditionType,
  PieLabelProps,
} from "./Charts.types";

/**
 * Formats the data for the bookings overview chart.
 *
 * @param {BookingsOverviewRecordType[]} inData
 * @param {string} dateFormat
 * @returns {CommonChartDataType[]}
 */
export function makeBookingsOverviewChartData(
  inData: BookingsOverviewRecordType[],
  dateFormat = "dd.MM.yyyy",
): CommonChartDataType[] {
  const outData: CommonChartDataType[] = [];

  for (const data of inData) {
    outData.push({
      date: data.date,
      valid: data.bookings_valid,
      canceled: data.bookings_canceled,
      waiting: data.bookings_waiting,
      name: format(parseISO(data.date), dateFormat),
    });
  }

  return Object.values(outData);
}

// https://stackoverflow.com/questions/6248666/how-to-generate-short-uid-like-ax4j9z-in-js
export function generateUID(): string {
  const firstPart = (Math.random() * 46656) | 0;
  const secondPart = (Math.random() * 46656) | 0;
  return ("000" + firstPart.toString(36)).slice(-3) + ("000" + secondPart.toString(36)).slice(-3);
}

export function getStatusCountChartColors(bar: string): string {
  const colors: { [key: string]: string } = {
    draft_count: getEventStatusColor(EventStatusEnum.Draft),
    offered_count: getEventStatusColor(EventStatusEnum.Offered),
    takes_place_count: getEventStatusColor(EventStatusEnum.TakesPlace),
    finished_count: getEventStatusColor(EventStatusEnum.Finished),
    canceled_count: getEventStatusColor(EventStatusEnum.Canceled),
  };
  return colors[bar];
}

export const getDateFormatByGranularity = (granularity: GranularityFilterEnum) => {
  switch (granularity) {
    case GranularityFilterEnum.Day:
      return "dd.MM.yyyy";
    case GranularityFilterEnum.Week:
      return "II/yyyy";
    case GranularityFilterEnum.Month:
      return "MMMM";
  }
};

/**
 * Returns the granularity enum value based on the given time range enum value.
 *
 * @param {string} period
 * @returns {GranularityFilterEnum.Day | GranularityFilterEnum.Week}
 */
export const getGranularityByPeriod = (period?: TimeRangeFilterEnum): GranularityFilterEnum => {
  switch (period) {
    case TimeRangeFilterEnum.Last7Days:
    case TimeRangeFilterEnum.Last30Days:
    case TimeRangeFilterEnum.CurrentMonth:
      return GranularityFilterEnum.Day;
    case TimeRangeFilterEnum.Last3Months:
      return GranularityFilterEnum.Week;
    default:
      return GranularityFilterEnum.Week;
  }
};

/**
 * Returns the number of the week in the year by the given date.
 *
 * @param {string} date
 * @returns {`${number}`}
 */
export const calendarWeekXAxisTickFormatter = (date: string) => `${getWeekNumber(new Date(date))}`;

/**
 * The x axis is labeled by the bucket it actually shows: a calendar week only where the
 * data *is* weekly. Labeling daily buckets with their week number repeats the same
 * "KW 33" on every day of that week, which reads as duplicated ticks.
 */
export const xAxisTickFormatterByGranularity =
  (granularity: GranularityFilterEnum) =>
  (date: string): string => {
    switch (granularity) {
      case GranularityFilterEnum.Week:
        return calendarWeekXAxisTickFormatter(date);
      case GranularityFilterEnum.Month:
        return format(parseISO(date), "MMM yy");
      default:
        return shortDateFromDatetime(date);
    }
  };

/** The "KW" caption belongs to weekly ticks only - a date needs no unit. */
export const xAxisLabelByGranularity = (granularity: GranularityFilterEnum): string | undefined =>
  granularity === GranularityFilterEnum.Week ? "KW" : undefined;

/**
 * Every second weekly tick still names its week; skipping daily ones would hide dates
 * that no neighbouring tick implies, so those are left to recharts.
 */
export const xAxisIntervalByGranularity = (granularity: GranularityFilterEnum): number | undefined =>
  granularity === GranularityFilterEnum.Week ? 1 : undefined;

/**
 * Returns a shorted amount string with dollar sign.
 *
 * @param {number} value
 * @returns {string}
 */
export const amountAxisTickFormatter = (value: number): string => `${shortenAmount(value)}${nonBreakingSpace}€`;

/**
 * Returns a variation of the base blue color.
 * If an index is passed it will return a color from the color palette.
 * 9 is the maximum index and 100% of the base color will be returned.
 * At 0 the color will be 10% of the base color.
 * If no index is passed it will return the complete color palette.
 *
 * @param {T} index
 * @returns {ExtendsNumberConditionType<T>}
 */
export function chartColorPalette<T extends number>(index?: T): ExtendsNumberConditionType<T> {
  const colors = Array(10)
    .fill("")
    .map((item: unknown, index) => {
      return `rgba(0,47,167,${(index + 1) / 10})`;
    });
  if (index && index >= 0 && index < 10) {
    return colors[index] as ExtendsNumberConditionType<T>;
  }
  return colors as ExtendsNumberConditionType<T>;
}

/**
 * Returns a percentage.
 *
 * @param {number} val
 * @param {number} total
 * @returns {number}
 */
export const getPercentage = (val: number, total: number) => {
  return val * (100 / total);
};

/**
 * Returns a translation for the given status.
 *
 * @param {EventStatusEnum} status
 * @param {InputChoiceType[]} translations
 * @returns {string}
 */
export const getStatusTranslation = (status: EventStatusEnum, translations: InputChoiceType[]): string => {
  for (const t of translations) {
    if (t.id == status) {
      return t.name;
    }
  }
  return status;
};

/** "KW 33 ab 11.08.2025" - the week plus the day it starts on. */
const weekDateLabel = (translate: TranslateFunction, label: string): string => {
  try {
    return translate("reports.calendar_week_with_date", {
      kw: getWeekNumber(new Date(label)),
      date: dateFromDatetime(label),
    });
  } catch {
    return label;
  }
};

export const TooltipFormatter = (translate: TranslateFunction) => {
  return {
    countAndSalesItemFormatter: (item: Payload<number, string>) => {
      let label = "";
      let value = "";
      if (item.dataKey == "count") {
        label = translate("reports.booking", { smart_count: item.value });
        value = (item.value && item.value.toString()) || "0";
      } else if (item.dataKey == "sales") {
        label = translate("reports.sales");
        value = renderCurrencyWithoutDecimals(item.value || 0);
      }
      return { label, value };
    },

    currencyLabelFormatter: (label: string) => {
      return `${label}${nonBreakingSpace}€`;
    },

    weekDateLabelFormatter: (label: string) => weekDateLabel(translate, label),

    /**
     * The tooltip names the same bucket the axis tick does: a calendar week plus its
     * first day where the data is weekly, the plain date otherwise.
     */
    bucketLabelFormatter: (granularity: GranularityFilterEnum) => (label: string) => {
      if (granularity === GranularityFilterEnum.Week) {
        return weekDateLabel(translate, label);
      }
      try {
        return granularity === GranularityFilterEnum.Month
          ? format(parseISO(label), "MMMM yyyy")
          : dateFromDatetime(label);
      } catch {
        return label;
      }
    },
  };
};

export const CartesianChartRenderHelper = (chartUid: string, defaultYAxisId: string) => {
  return {
    // CartesianChart methods
    renderLines: (lines: ChartLineType[]): ReactElement[] => {
      return lines.map((line, index) => (
        <Line
          key={`${chartUid}-cartesian-chart-line-${index}`}
          yAxisId={line.yAxisId ? line.yAxisId : defaultYAxisId}
          type={line.type ? line.type : "monotone"}
          dataKey={line.dataKey}
          stroke={line.color}
          strokeWidth={line.strokeWidth || 1.2}
          dot={false}
        />
      ));
    },

    renderBars: (bars: ChartBarType[]): ReactElement[] => {
      const defaultStackId = generateUID();

      return bars.map((bar, index) => {
        const stroke = bar.stroke || bar.color;
        const fill = bar.stroke ? "#fff" : bar.color;

        return (
          <Bar
            dataKey={bar.dataKey}
            stackId={bar.stackId ? bar.stackId : defaultStackId}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
            key={`${chartUid}-cartesian-chart-bar-${index}`}
            yAxisId={bar.yAxisId ? bar.yAxisId : defaultYAxisId}
            maxBarSize={40}
            radius={1}
          />
        );
      });
    },

    renderAreas: (areas: ChartAreaType[]): ReactElement[] => {
      return areas.map((area, index) => (
        <Area
          key={`${chartUid}-cartesian-chart-area-${index}`}
          yAxisId={area.yAxisId ? area.yAxisId : defaultYAxisId}
          type={area.type ? area.type : "monotone"}
          dataKey={area.dataKey}
          stackId={area.stackId ? area.stackId : undefined}
          stroke={area.stroke || area.color}
          fill={area.color}
        />
      ));
    },

    renderDefs: (defs: ChartDefsType): ReactElement => {
      const renderLinearGradient = (gradient: ChartGradientType) => {
        const positions = {
          ...{
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          ...gradient.positions,
        };

        return (
          <linearGradient
            key={`${chartUid}-ChartGradient-${gradient.id}`}
            id={gradient.id}
            x1={positions.x1}
            y1={positions.y1}
            x2={positions.x2}
            y2={positions.y2}
            spreadMethod={gradient.repeat ? "repeat" : "pad"}
          >
            <stop
              offset={gradient.startOffset}
              stopColor={gradient.startColor}
              stopOpacity={gradient.startOpacity || 1}
            />
            <stop offset={gradient.endOffset} stopColor={gradient.endColor} stopOpacity={gradient.endOpacity || 1} />
          </linearGradient>
        );
      };

      const defsList = [];
      if (defs.gradients) {
        defsList.push(defs.gradients.map((gradient) => renderLinearGradient(gradient)));
      }

      if (defsList.length) {
        return <defs key={`${chartUid}-Chart-defs`}>{defsList}</defs>;
      }

      return <></>;
    },

    renderReferenceLines: (referenceLines: ChartReferenceLineType[]): ReactElement[] =>
      referenceLines.map(({ label, ...rest }, index) => (
        <ReferenceLine
          key={`${chartUid}-ChartReferenceLine-${index}`}
          stroke={rest.color}
          yAxisId="left"
          label={label as string | undefined}
          {...rest}
        />
      )),
  };
};

export const PieChartRenderHelper = (chartUid: string) => {
  return {
    renderPies: (data: ReportByMainDataApiDataType[], pieLimit?: number) => {
      const pies = [];
      let i = 1;
      for (const d of data) {
        if (pieLimit && i > pieLimit) {
          break;
        }
        pies.push(<Cell key={`${chartUid}-cell-${i}`} fill={d.color ? d.color : chartColorPalette(i % 10)} />);
        i++;
      }
      return pies;
    },

    renderPieLabel: (
      props: PieLabelProps,
      currentFilter: string,
      pieLabelFormatter?: (name: string, filter: string) => string,
      pieLabelValueFormatter?: (name: string, filter: string) => string,
    ) => {
      const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, payload, percent = 0, value = 0 } = props;

      const RADIAN = Math.PI / 180;
      const innerLineLength = 20;
      const outerLineLength = 10;
      const pieLineGap = 5;
      const lineHeight = 16;
      const textLineGap = 6;
      const circleSize = 3;
      const textWidth = 100;
      const lineColor = "#666";
      const labelColor = "#666";

      if (percent <= 0.03) {
        return <></>;
      }

      const label = pieLabelFormatter ? pieLabelFormatter(payload?.name ?? "", currentFilter) : (payload?.name ?? "");
      const labelValue = pieLabelValueFormatter ? pieLabelValueFormatter(String(value), currentFilter) : value;

      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);
      const sx = cx + (outerRadius + pieLineGap) * cos;
      const sy = cy + (outerRadius + pieLineGap) * sin;
      const mx = cx + (outerRadius + innerLineLength) * cos;
      const my = cy + (outerRadius + innerLineLength) * sin;
      const ex = mx + (cos >= 0 ? 1 : -1) * outerLineLength;
      const ey = my;
      const textAnchor = cos >= 0 ? "start" : "end";

      return (
        <g style={{ fontSize: "0.8rem" }}>
          <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={lineColor} fill="none" />
          <circle cx={ex} cy={ey} r={circleSize} fill={lineColor} stroke="none" />
          <foreignObject
            x={ex + (cos >= 0 ? 1 : -1) * textLineGap - (textAnchor == "end" ? textWidth : 0)}
            y={ey - lineHeight}
            textAnchor={textAnchor}
            width={textWidth}
            height={lineHeight}
          >
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: `${textWidth}px`,
                color: labelColor,
                textAlign: textAnchor == "end" ? "right" : "left",
              }}
            >
              {label}
            </div>
          </foreignObject>
          <text x={ex + (cos >= 0 ? 1 : -1) * textLineGap} y={ey} dy={10} textAnchor={textAnchor} fill={labelColor}>
            {`${labelValue} (${(percent * 100).toFixed(2)}%)`}
          </text>
        </g>
      );
    },
  };
};
